import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FEES, FIRM, NO_CHARGE_GUARANTEE, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "How should I compare average conveyancing fees?",
    a: "An average fee is only a starting point. Compare written quotes for the same property and the same work. Ask whether GST, contract review, searches, PEXA and registration fees are included or charged separately. A unit, commercial property or unusual contract can need different work from a standard house sale.",
  },
  {
    q: "How much does conveyancing cost in Queensland?",
    a: `Legal fees for a standard home sale or purchase in Queensland usually run from about $800 to $2,500. At Coastside Law a sale is a fixed ${FEES.sale} and a purchase is a fixed ${FEES.purchase}, both including GST. On top of the legal fee you pay searches and government fees, and buyers pay transfer duty.`,
  },
  {
    q: "Why is buying more expensive than selling?",
    a: "The buyer's side does more of the work: reviewing the contract and special conditions, ordering searches, tracking finance and building and pest dates, handling transfer duty and registering the transfer. The seller's side is mainly the contract, disclosure and settlement.",
  },
  {
    q: "What costs come on top of the legal fee?",
    a: "Searches (title, council rates and water, body corporate for units), the electronic settlement (PEXA) fee and Titles Office registration fees. For a buyer these usually add roughly $450 to $900, before transfer duty. Building and pest inspections are paid directly to the inspector.",
  },
  {
    q: "What is the biggest cost when buying?",
    a: "Transfer duty (stamp duty). On most homes it's far larger than every other cost put together, although first home buyers may get a concession or exemption. Use our stamp duty calculator for a quick estimate.",
  },
  {
    q: "What if the contract falls over?",
    a: `${NO_CHARGE_GUARANTEE.short} You only pay for searches already ordered, at cost.`,
  },
  {
    q: "Is a cheap conveyancer a false economy?",
    a: "Sometimes. A low headline price can leave out searches, or come with extra charges for things like PEXA, file setup or calls. Ask for the full fixed fee in writing, and ask who will actually handle your file. With us it's a solicitor.",
  },
  {
    q: "When do I pay?",
    a: "Generally at settlement: from the sale proceeds when you sell, or with the balance of the price when you buy. Some searches are paid earlier because they're ordered at the start.",
  },
];

export const Route = createFileRoute("/conveyancing-cost-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("How Much Does Conveyancing Cost in QLD? | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          `Conveyancing costs in Queensland explained: typical legal fees, searches, PEXA and duty. Coastside Law charges a fixed ${FEES.sale} to sell and ${FEES.purchase} to buy, incl. GST.`,
        ),
      },
      { property: "og:title", content: "How much does conveyancing cost in Queensland?" },
      {
        property: "og:description",
        content: fitDescription("Typical fees, the costs on top, and our fixed prices, in plain English."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://coastsidelaw.com.au/conveyancing-cost-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/conveyancing-cost-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Conveyancing costs"
      title="How much does conveyancing cost in Queensland?"
      lead={`Usually $800 to $2,500 in legal fees, plus searches and, for buyers, transfer duty. With us it's a fixed ${FEES.sale} to sell and ${FEES.purchase} to buy, including GST.`}
      boxTitle="The short version"
      boxItems={[
        `Coastside Law: sale ${FEES.sale}, purchase ${FEES.purchase}, fixed, incl. GST`,
        "Typical Queensland legal fees: about $800 to $2,500",
        "Searches, PEXA and registration: roughly $450 to $900 for buyers",
        "Transfer duty is usually the biggest cost when buying",
        "Contract falls over under a standard condition? No professional fee",
      ]}
      faqs={FAQS}
      links={[
        { to: "/fees", label: "Full fee breakdown" },
        { to: "/stamp-duty-calculator-qld", label: "Stamp duty calculator" },
        { to: "/buyers-solicitor-qld", label: "Buyer's solicitor" },
        { to: "/conveyancing", label: "Conveyancing" },
        { to: "/guides/how-much-does-conveyancing-cost-in-queensland", label: "Checklist for comparing written quotes" },
      ]}
      formHeading="Get a fixed-fee quote"
      ctaTitle="Want a firm price?"
      ctaBody={`Call ${FIRM.phone}. We'll confirm your fixed fee and likely searches up front.`}
    />
  ),
});
